import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Crown,
  Shield,
  ShieldAlert,
  UserMinus,
  UserPlus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Bot,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const OwnershipRoles = () => {
  const { token, isOwner } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [actionUser, setActionUser] = useState(null);
  const [actionType, setActionType] = useState(null); // "demote" or "delete"
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (isOwner) {
      fetchGovernanceData();
    }
  }, [isOwner]);

  const fetchGovernanceData = async () => {
    try {
      const response = await axios.get(`${API}/admin/governance/super-admins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (err) {
      console.error("Error fetching governance data:", err);
      toast.error("Failed to load governance data");
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async () => {
    if (!selectedAdmin) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/governance/promote-to-super-admin`,
        { user_id: selectedAdmin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("User promoted to SUPER_ADMIN");
      setSelectedAdmin("");
      fetchGovernanceData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to promote user");
    } finally {
      setProcessing(false);
    }
  };

  const handleDemote = async () => {
    if (!actionUser) return;
    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/governance/demote-super-admin/${actionUser.user_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Demoted ${actionUser.email} to ADMIN`);
      setActionUser(null);
      setActionType(null);
      fetchGovernanceData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to demote user");
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!actionUser) return;
    setProcessing(true);
    try {
      await axios.delete(
        `${API}/admin/governance/super-admin/${actionUser.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted SUPER_ADMIN ${actionUser.email}`);
      setActionUser(null);
      setActionType(null);
      fetchGovernanceData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Access denied for non-owners
  if (!isOwner) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Owner Access Required
          </h3>
          <p className="text-red-700">
            Only the platform OWNER can access role governance.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-amber-100 rounded-lg">
          <Crown className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Ownership & Roles</h2>
          <p className="text-sm text-slate-600">
            Manage SUPER_ADMIN accounts and platform governance
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Super Admins</p>
                <p className="text-2xl font-bold text-purple-600">
                  {data?.super_admin_count || 0}
                  <span className="text-sm font-normal text-slate-400"> / 3 max</span>
                </p>
              </div>
              <Shield className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Eligible for Promotion</p>
                <p className="text-2xl font-bold text-blue-600">
                  {data?.eligible_admins?.length || 0}
                </p>
              </div>
              <UserPlus className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Status</p>
                <div className="flex items-center gap-2">
                  {data?.can_add_more ? (
                    <Badge className="bg-green-100 text-green-700">Can Add More</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">At Limit</Badge>
                  )}
                </div>
              </div>
              <Info className="w-8 h-8 text-slate-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promote Admin Section */}
      {data?.can_add_more && data?.eligible_admins?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Promote Admin to Super Admin
            </CardTitle>
            <CardDescription>
              Select an ADMIN to promote to SUPER_ADMIN (max 3 allowed)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an admin to promote" />
                  </SelectTrigger>
                  <SelectContent>
                    {data.eligible_admins.map((admin) => (
                      <SelectItem key={admin.user_id} value={admin.user_id}>
                        {admin.name} ({admin.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handlePromote}
                disabled={!selectedAdmin || processing}
                className="bg-green-600 hover:bg-green-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Promote
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Super Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Current Super Admins
          </CardTitle>
          <CardDescription>
            {data?.super_admin_count} SUPER_ADMIN account(s). Minimum 1 required.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.super_admins?.map((admin) => (
                <TableRow key={admin.user_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{admin.name}</p>
                      <p className="text-sm text-slate-500">{admin.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(admin.created_at)}</TableCell>
                  <TableCell>{formatDate(admin.last_login_at)}</TableCell>
                  <TableCell>
                    {admin.is_bootstrap_account ? (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Bot className="w-3 h-3 mr-1" />
                        Bootstrap
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-700">Manual</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setActionUser(admin);
                          setActionType("demote");
                        }}
                        disabled={!data?.can_remove}
                        title={!data?.can_remove ? "Min 1 SUPER_ADMIN required" : "Demote to ADMIN"}
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setActionUser(admin);
                          setActionType("delete");
                        }}
                        disabled={!data?.can_remove}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={!data?.can_remove ? "Min 1 SUPER_ADMIN required" : "Delete account"}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {data?.super_admins?.some((a) => a.is_bootstrap_account) && (
            <Alert className="mt-4 border-amber-200 bg-amber-50">
              <Bot className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Bootstrap Account Detected:</strong> The account marked as
                &quot;Bootstrap&quot; was created automatically during initial setup. You can
                demote or delete it once you have promoted another admin.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={!!actionUser && !!actionType}
        onOpenChange={() => {
          setActionUser(null);
          setActionType(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {actionType === "delete" ? (
                <Trash2 className="w-5 h-5 text-red-600" />
              ) : (
                <UserMinus className="w-5 h-5 text-amber-600" />
              )}
              {actionType === "delete" ? "Delete Super Admin?" : "Demote Super Admin?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === "delete" ? (
                <>
                  This will <strong>permanently delete</strong> the account for{" "}
                  <strong>{actionUser?.email}</strong>, invalidate all their sessions,
                  and remove all their data. This action cannot be undone.
                </>
              ) : (
                <>
                  This will demote <strong>{actionUser?.email}</strong> from SUPER_ADMIN
                  to ADMIN. They will lose user management privileges but retain content
                  management access.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={actionType === "delete" ? handleDelete : handleDemote}
              disabled={processing}
              className={
                actionType === "delete"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-amber-600 hover:bg-amber-700"
              }
            >
              {processing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : actionType === "delete" ? (
                <Trash2 className="w-4 h-4 mr-2" />
              ) : (
                <UserMinus className="w-4 h-4 mr-2" />
              )}
              {actionType === "delete" ? "Delete" : "Demote"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OwnershipRoles;
